import React from "react";
import { useFieldArray } from "react-hook-form";

import { FormStepProps } from "../../../screens/MadFormScreen";

import UserSearch from "../../utils/search/UserSearch";
import {
  MadFullFragment,
  UserSummaryFragment,
} from "../../../graphql/fragments";
import useTranslations from "../../../hooks/useTranslations";
import { Avatar, Button, Checkbox } from "../../wombat-ui";
import { CloseIcon } from "../../icons";

export const SelectedSupervisor: React.FC<
  Pick<FormStepProps, "register"> & {
    user: UserSummaryFragment;
    index: number;
    isLast: boolean;
    baseId: string;
    onDelete: () => void;
    defaultChecked: boolean;
  }
> = ({ user, baseId, isLast, index, register, onDelete, defaultChecked }) => {
  const t = useTranslations();

  return (
    <div
      className={`flex items-center justify-between w-full max-w-2xl ${
        index === 0 && "pt-2"
      } pb-2 ${!isLast && "border-b"} border-light-500`}
    >
      <input
        type="hidden"
        value={user.id}
        ref={register()}
        id={`madSupervisorsAttributes[${index}].userId`}
        name={`madSupervisorsAttributes[${index}].userId`}
      />
      <div className="flex items-center">
        <Avatar src={user.avatar} alt="" size={6} type="user" />
        <p className="ml-4 text-sm font-medium text-dark-100">{user.name}</p>
      </div>
      <div className="flex items-center">
        <Checkbox
          variant="boxed"
          label={t("mads.receiveEmailNotification") as string}
          ref={register()}
          id={`madSupervisorsAttributes[${index}].emailNotificationsEnabled`}
          name={`madSupervisorsAttributes[${index}].emailNotificationsEnabled`}
          defaultChecked={defaultChecked}
        />
        <Button
          scale="xs"
          variant="ghost"
          icon={CloseIcon}
          id={`${baseId}${user.id}-user-remove`}
          onClick={onDelete}
        >
          <span className="sr-only">
            {t("forms.actions.removeEntity", { entity: user.name })}
          </span>
        </Button>
      </div>
    </div>
  );
};

const MadSupervisorsForm: React.FC<
  Omit<FormStepProps, "onNext"> & { defaultValues?: MadFullFragment }
> = ({ register, control, defaultValues }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "madSupervisorsAttributes",
    keyName: "key",
  });

  return (
    <UserSearch
      defaultValue={
        fields.length > 0
          ? (fields.map(({ userId }) => userId) as string[])
          : defaultValues?.madSupervisors.map(({ user }) => user.id)
      }
      baseId="madSupervisorsAttributes"
      onSelect={({ id }) => {
        append({ userId: id, emailNotificationsEnabled: true });
      }}
      onRemove={({ id }) => {
        remove(fields.findIndex(({ userId }) => id === userId));
      }}
    >
      {({ users, handleUserDelete }) =>
        fields.map((field, index) => {
          const user = users.find(({ id }) => id === field.userId);

          if (!user) {
            return null;
          }

          return (
            <SelectedSupervisor
              key={field.key}
              user={user}
              baseId={"madSupervisorsAttributes"}
              index={index}
              isLast={index === fields.length - 1}
              register={register}
              onDelete={() => {
                handleUserDelete(user.id);
              }}
              defaultChecked={field.emailNotificationsEnabled}
            />
          );
        })
      }
    </UserSearch>
  );
};

export default React.memo(MadSupervisorsForm);
